import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Router } from '@angular/router';
import { Observable, filter, map, of, forkJoin, switchMap, tap, from, catchError } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ProjectsApiResponse,
  ProjectByCodeResponse,
  ProjectStatusResponse,
  ProjectTypesResponse,
  RegionsResponse,
  CompaniesResponse,
  BusinessUnitsResponse,
  CountriesResponse,
  ProductTypesResponse,
  ResponsiblesRolesResponse,
  ProjectDetailResponse,
  InvoicesApiResponse,
  InvoiceStatesResponse
} from '../core/interfaces/project-api.interface';

const ID_TOKEN_SCOPES = ['openid', 'profile'];

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private http = inject(HttpClient);
  private router = inject(Router);
  private msalInitialized = false;

  private idTokenFromLogin: string | null = null;

  constructor() {
    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe((event: EventMessage) => {
        const result = event.payload as AuthenticationResult;
        console.log('Login successful', result);
        if (result?.idToken) {
          this.idTokenFromLogin = result.idToken;
        }
        this.fetchProjectsAfterLogin(event);
      });
    this.initializeMsal();
  }

  async initializeMsal(): Promise<void> {
    if (!this.msalInitialized && typeof (this.msalService as unknown as { initialize?: () => Promise<void> }).initialize === 'function') {
      await (this.msalService as unknown as { initialize: () => Promise<void> }).initialize();
      this.msalInitialized = true;
    } else {
      this.msalInitialized = true;
    }
  }

  private getTokenFromMsal(): Observable<string | null> {
    if (this.idTokenFromLogin) return of(this.idTokenFromLogin);
    const account = this.getAccount();
    if (!account) return of(null);
    return from(
      this.msalService.instance.acquireTokenSilent({
        account,
        scopes: ID_TOKEN_SCOPES
      })
    ).pipe(
      map((result: AuthenticationResult) => {
        const token = result.idToken ?? null;
        if (token) this.idTokenFromLogin = token;
        return token;
      }),
      catchError(() => of(null))
    );
  }

  private getApiBaseUrl(): string {
    return environment.accountingApi.baseUrl;
  }

  private buildApiUrl(path: string): string {
    const base = this.getApiBaseUrl();
    const sep = base.endsWith('/') || path.startsWith('/') ? '' : '/';
    return `${base}${sep}${path}`;
  }

  private getToken(): Observable<string | null> {
    const hardcoded = (environment as { hardcodedToken?: string }).hardcodedToken?.trim();
    if (hardcoded) return of(hardcoded);
    return this.getTokenFromMsal();
  }

  private fetchWithToken<T>(url: string, freshPayload?: AuthenticationResult): Observable<T | null> {
    const token$ = freshPayload?.idToken
      ? of(freshPayload.idToken).pipe(tap((t) => { if (t) this.idTokenFromLogin = t; }))
      : this.getToken();

    return token$.pipe(
      switchMap((idToken) => {
        if (!idToken) return of(null);
        return this.http.get<T>(url, { headers: { Authorization: `Bearer ${idToken}` } });
      })
    );
  }

  fetchProjects(freshPayload?: AuthenticationResult): Observable<ProjectsApiResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.projects);
    return this.fetchWithToken<ProjectsApiResponse>(url, freshPayload);
  }

  fetchProjectByCode(code: string): Observable<ProjectByCodeResponse | null> {
    const url = this.buildApiUrl(`${environment.accountingApi.endpoints.projectByCode}/${encodeURIComponent(code)}`);
    return this.fetchWithToken<ProjectByCodeResponse>(url);
  }

  fetchInvoicesByProjectCode(code: string): Observable<InvoicesApiResponse | null> {
    const basePath = environment.accountingApi.endpoints.invoices;
    const url = `${this.buildApiUrl(basePath)}/${encodeURIComponent(code)}`;
    return this.fetchWithToken<InvoicesApiResponse>(url);
  }

  fetchInvoiceStates(): Observable<InvoiceStatesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.invoiceStates);
    return this.fetchWithToken<InvoiceStatesResponse>(url);
  }

  fetchProjectsAndDetails(): Observable<ProjectDetailResponse[]> {
    return this.fetchProjects().pipe(
      tap((res) => console.log('[AuthService] API Projects:', res)),
      switchMap((res) => {
        const body = res as ProjectsApiResponse | null;
        const codes = (body?.data ?? []).map((item) => item.code).filter((c): c is string => !!c);
        if (codes.length === 0) return of([]);
        return forkJoin(codes.map((code) => this.fetchProjectByCode(code)));
      }),
      map((details) =>
        (details ?? [])
          .map((item) => {
            const wrapped = item as { data?: ProjectDetailResponse };
            return wrapped?.data ?? (item as ProjectDetailResponse);
          })
          .filter((p): p is ProjectDetailResponse => p?.code != null)
      )
    );
  }

  fetchProjectStatus(): Observable<ProjectStatusResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.projectStatus);
    return this.fetchWithToken<ProjectStatusResponse>(url);
  }

  fetchProjectTypes(): Observable<ProjectTypesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.projectTypes);
    return this.fetchWithToken<ProjectTypesResponse>(url);
  }

  fetchRegions(): Observable<RegionsResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.regions);
    return this.fetchWithToken<RegionsResponse>(url);
  }

  fetchCompanies(): Observable<CompaniesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.companies);
    return this.fetchWithToken<CompaniesResponse>(url);
  }

  fetchBusinessUnits(): Observable<BusinessUnitsResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.businessUnits);
    return this.fetchWithToken<BusinessUnitsResponse>(url);
  }

  fetchCountries(): Observable<CountriesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.countries);
    return this.fetchWithToken<CountriesResponse>(url);
  }

  fetchProductTypes(): Observable<ProductTypesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.productTypes);
    return this.fetchWithToken<ProductTypesResponse>(url);
  }

  fetchResponsiblesRoles(): Observable<ResponsiblesRolesResponse | null> {
    const url = this.buildApiUrl(environment.accountingApi.endpoints.responsiblesRoles);
    return this.fetchWithToken<ResponsiblesRolesResponse>(url);
  }

  fetchServiceTypes(productType: number): Observable<unknown> {
    const path = `${environment.accountingApi.endpoints.servicesTypes}/${encodeURIComponent(String(productType))}`;
    const url = this.buildApiUrl(path);
    return this.fetchWithToken<unknown>(url);
  }

  fetchSystemDataForProjectDetail(): Observable<{
    projectStatus: ProjectStatusResponse | null;
    projectTypes: ProjectTypesResponse | null;
    regions: RegionsResponse | null;
    companies: CompaniesResponse | null;
    businessUnits: BusinessUnitsResponse | null;
    countries: CountriesResponse | null;
    productTypes: ProductTypesResponse | null;
    responsiblesRoles: ResponsiblesRolesResponse | null;
  }> {
    return forkJoin({
      projectStatus: this.fetchProjectStatus(),
      projectTypes: this.fetchProjectTypes(),
      regions: this.fetchRegions(),
      companies: this.fetchCompanies(),
      businessUnits: this.fetchBusinessUnits(),
      countries: this.fetchCountries(),
      productTypes: this.fetchProductTypes(),
      responsiblesRoles: this.fetchResponsiblesRoles()
    });
  }

  private fetchProjectsAfterLogin(event: EventMessage): void {
    const payload = event.payload as AuthenticationResult;
    this.fetchProjects(payload).subscribe({
      next: (response) => console.log('[API Projects] Respuesta tras login:', response),
      error: (err) => console.error('[API Projects] Error:', err)
    });
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  /**
   * Get the current user account
   */
  getAccount() {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  login(redirectUrl?: string): void {
    if (redirectUrl) {
      sessionStorage.setItem('postLoginRedirectUrl', redirectUrl);
    }
    Promise.resolve(this.initializeMsal()).then(() => {
      this.msalService.loginRedirect();
    });
  }
  logout(): void {
    this.idTokenFromLogin = null;
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/login`,
      onRedirectNavigate: () => {
        // Return false = no ir a Microsoft logout → solo limpiamos caché local
        // Redirigimos manualmente a /login
        this.router.navigateByUrl('/login');
        return false;
      }
    });
  }

  /**
   * Logout con redirect a Microsoft (cierra todas las cuentas) - usar solo si se necesita.
   */
  logoutFull(): void {
    this.idTokenFromLogin = null;
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/login`
    });
  }

  /**
   * Get interaction status observable
   */
  getInteractionStatus(): Observable<InteractionStatus> {
    return this.msalBroadcastService.inProgress$;
  }

  /**
   * Check if interaction is in progress
   */
  isInteractionInProgress(): Observable<boolean> {
    return this.msalBroadcastService.inProgress$.pipe(
      map(status => status !== InteractionStatus.None)
    );
  }
}
